import { motion, AnimatePresence } from "framer-motion";
import { ActionCard, ActionItem } from "./ActionCard";
import { AlertTriangle, Calendar, Clock, CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";

interface TimelineGroup {
  key: string;
  label: string;
  icon: React.ElementType;
  iconColor: string;
  items: ActionItem[];
}

interface ActionsTimelineProps {
  items: ActionItem[];
  onComplete: (id: string) => void;
  onDismiss: (id: string) => void;
  onView: (item: ActionItem) => void;
  updatingId: string | null;
  onAlreadyPaid?: (id: string) => void;
}

export function ActionsTimeline({ 
  items, 
  onComplete, 
  onDismiss, 
  onView,
  updatingId,
  onAlreadyPaid
}: ActionsTimelineProps) {
  // Group items by urgency period
  const groupItems = (items: ActionItem[]): TimelineGroup[] => {
    const expired: ActionItem[] = [];
    const thisWeek: ActionItem[] = [];
    const thisMonth: ActionItem[] = [];
    const later: ActionItem[] = [];

    items.forEach(item => {
      const days = item.daysUntilExpiry ?? (
        item.dueDate 
          ? Math.ceil((new Date(item.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
          : 999
      );

      if (days < 0) {
        expired.push(item);
      } else if (days <= 7) {
        thisWeek.push(item);
      } else if (days <= 30) {
        thisMonth.push(item);
      } else {
        later.push(item);
      }
    });

    const groups: TimelineGroup[] = [];

    if (expired.length > 0) {
      groups.push({
        key: 'expired',
        label: 'En retard',
        icon: AlertTriangle,
        iconColor: 'text-destructive',
        items: expired
      });
    }

    if (thisWeek.length > 0) {
      groups.push({
        key: 'thisWeek',
        label: 'Cette semaine',
        icon: Clock,
        iconColor: 'text-warning',
        items: thisWeek
      });
    }

    if (thisMonth.length > 0) {
      groups.push({
        key: 'thisMonth',
        label: 'Ce mois',
        icon: Calendar,
        iconColor: 'text-primary',
        items: thisMonth
      });
    }

    if (later.length > 0) {
      groups.push({
        key: 'later',
        label: 'Plus tard',
        icon: CalendarDays,
        iconColor: 'text-muted-foreground',
        items: later
      });
    }

    return groups;
  };

  const groups = groupItems(items);

  if (items.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center justify-center py-12 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
          >
            ✓
          </motion.div>
        </div>
        <h3 className="text-lg font-medium text-foreground">Tout est en ordre !</h3>
        <p className="text-sm text-muted-foreground mt-1">Aucune action en attente</p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      <AnimatePresence mode="popLayout">
        {groups.map((group, groupIndex) => (
          <motion.div
            key={group.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ delay: groupIndex * 0.1 }}
            className="relative"
          >
            {/* Group Header */}
            <div className="flex items-center gap-2 mb-3 sticky top-0 bg-background/95 backdrop-blur-sm py-2 z-10">
              <div className={cn(
                "w-8 h-8 rounded-lg flex items-center justify-center",
                group.key === 'expired' ? "bg-destructive/10" :
                group.key === 'thisWeek' ? "bg-warning/10" :
                group.key === 'thisMonth' ? "bg-primary/10" :
                "bg-muted"
              )}>
                <group.icon className={cn("w-4 h-4", group.iconColor)} />
              </div>
              <h3 className="text-sm font-medium text-foreground">{group.label}</h3>
              <span className="text-xs text-muted-foreground">({group.items.length})</span>
            </div>

            {/* Timeline Line */}
            <div className="relative pl-4 ml-4 border-l-2 border-muted space-y-3">
              <AnimatePresence mode="popLayout">
                {group.items.map((item, itemIndex) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: itemIndex * 0.05 }}
                    className="relative"
                  >
                    {/* Timeline dot */}
                    <div className={cn(
                      "absolute -left-[21px] top-6 w-3 h-3 rounded-full border-2 bg-background",
                      group.key === 'expired' ? "border-destructive" :
                      group.key === 'thisWeek' ? "border-warning" :
                      group.key === 'thisMonth' ? "border-primary" :
                      "border-muted-foreground"
                    )} />
                    
                    <ActionCard
                      item={item}
                      onComplete={onComplete}
                      onDismiss={onDismiss}
                      onView={onView}
                      isUpdating={updatingId === item.id}
                      onAlreadyPaid={onAlreadyPaid}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
